import { getFromS3, getAWSUrl , downloadSignatureAsBase64, getBundleDownloadUrl} from './AWSService';
import { store } from '@/store';
import { AlertBuilder } from '@/utilities/AlertBuilder';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { compareVersions } from '@/utilities/Version';
import { logError, logInfo, writeFileToDataDirectory, readTextFileFromDataDirectory, writeImageBlobToDataDirectory } from '@/utilities/Filesystem';
import {reloadLanguage, reloadManifestMetaData, checkAndUpdateManifestWithSpeedCheck, shouldShowRestartToast, markRestartToastShown, scheduleLanguageDownloadInBackground} from '@/storage/DataStorage';
import translate from "../views/translate";
import { Device } from '@capacitor/device';
import { networkSpeedService } from './NetworkSpeedService';
import { createLoadSpinner } from './ShareService';

const MANIFEST_FILE = 'bundleManifest.json';

/** S3 keys under variant for app icon by platform. All under <variant>/icons/... */
const APP_ICON_S3_KEYS = {
    ios: 'icons/ios/AppIcon.appiconset/Icon-App-60x60@3x.png',
    androidSecure: 'icons/android/secure/playstore-icon.png',
    androidPublic: 'icons/android/public/playstore-icon.png'
} as const;
const APP_ICON_DATA_FILE = 'appIcon.png';

/**
 * PWA only: variant from request URL subdomain (e.g. discoverystudies.patternsites.com → discoverystudies).
 * Use when store.state.variant is not set yet (before language load). Not used for native mobile.
 */
export function getPwaVariantFromHostname(): string {
    if (typeof window === 'undefined' || !window.location || !window.location.hostname) return '';
    const host = window.location.hostname;
    return host === 'localhost' ? 'demopattern' : host.split('.')[0];
}

interface Manifest {
    version: string;
    bundleUrl?: string;
    languageVersion: number;
    downloadApk: boolean;
    contentVersion?: string | number;
}

interface PendingBundleInfo {
    bundleId: string;
    version: string;
    downloadedAt: number;
}

export class LiveUpdateService {
    private readonly PENDING_BUNDLE_FILE = 'pendingBundle.json';
    private readonly LOOP_DETECTION_FILE = 'bundleLoopDetection.json';
    private readonly BUNDLE_APPLICATION_FILE = 'lastBundleApplication.json';
    
    // Singleton guard to prevent multiple update checks in the same session
    private static updateCheckInProgress = false;
    private static updateCheckCompleted = false;
    private static bundleApplicationAttempted = false;

    /**
     * Detects if we're in a restart loop and should bail out.
     * Uses two mechanisms:
     * 1. Tracks restart timestamps - if 4+ restarts in 30 seconds, it's a loop
     * 2. Tracks bundle application time - if a bundle was applied in the last 60 seconds, refuse to apply again
     */
    public async isInRestartLoop(): Promise<boolean> {
        logInfo('[Loading] 13% - loop-check-start', 0);
        try {
            const { Filesystem, Directory } = await import('@capacitor/filesystem');
            const now = Date.now();
            let restarts: number[] = [];
            
            // Try to read existing restart timestamps
            try {
                const result = await Filesystem.readFile({
                    path: this.LOOP_DETECTION_FILE,
                    directory: Directory.Data,
                    encoding: 'utf8' as any
                });
                const data = JSON.parse(result.data as string);
                restarts = Array.isArray(data.restarts) ? data.restarts : [];
            } catch (error) {
                // File doesn't exist, start fresh
                restarts = [];
            }
            
            // Add current timestamp
            restarts.push(now);
            
            // Keep only restarts from last 30 seconds
            const thirtySecondsAgo = now - 30000;
            restarts = restarts.filter(t => t > thirtySecondsAgo);
            
            // Log restart count with Loading format for console visibility
            logInfo(`[Loading] 13% - restarts-${restarts.length}`, 0);
            store.dispatch("setLoadingValue", { value: 0.13, slug: `restarts-${restarts.length}` });
            
            // Save updated timestamps
            await Filesystem.writeFile({
                path: this.LOOP_DETECTION_FILE,
                directory: Directory.Data,
                data: JSON.stringify({ restarts }),
                encoding: 'utf8' as any
            });
            
            // If we've restarted 4+ times in 30 seconds, we're in a loop
            const isLoop = restarts.length >= 4;
            
            if (isLoop) {
                logInfo(`[Loading] 13% - LOOP-${restarts.length}`, 0);
                store.dispatch("setLoadingValue", { value: 0.13, slug: `LOOP-${restarts.length}` });
                
                // Clear ALL update-related files to break the loop
                const filesToClear = [
                    this.PENDING_BUNDLE_FILE,
                    'skipUpdateCheck.flag',
                    this.BUNDLE_APPLICATION_FILE,
                    this.LOOP_DETECTION_FILE
                ];
                
                for (const file of filesToClear) {
                    try {
                        await Filesystem.deleteFile({
                            path: file,
                            directory: Directory.Data
                        });
                    } catch (e) { /* ignore */ }
                }
            }
            
            return isLoop;
        } catch (error) {
            logInfo('[Loading] 13% - loop-check-error', 0);
            logError(error, '[isInRestartLoop] Error checking for restart loop');
            return false;
        }
    }
    
    /**
     * Checks if a bundle was applied recently (within 60 seconds).
     * This is a secondary circuit breaker to prevent loops.
     */
    private async wasBundleAppliedRecently(): Promise<boolean> {
        try {
            const content = await readTextFileFromDataDirectory(this.BUNDLE_APPLICATION_FILE);
            if (!content) return false;
            
            const data = JSON.parse(content);
            const appliedAt = data.appliedAt || 0;
            const bundleId = data.bundleId || '';
            const now = Date.now();
            const timeSinceApplication = now - appliedAt;
            
            logInfo(`[wasBundleAppliedRecently] Last application: bundleId=${bundleId}, ${timeSinceApplication}ms ago`, 0);
            
            // If a bundle was applied within the last 60 seconds, refuse to apply another
            if (timeSinceApplication < 60000) {
                logInfo(`[wasBundleAppliedRecently] Bundle was applied ${timeSinceApplication}ms ago - COOLDOWN ACTIVE`, 0);
                return true;
            }
            
            return false;
        } catch (error) {
            // File doesn't exist or can't be read
            return false;
        }
    }
    
    /**
     * Records that a bundle was just applied.
     */
    private async recordBundleApplication(bundleId: string): Promise<void> {
        try {
            const data = {
                bundleId,
                appliedAt: Date.now()
            };
            await writeFileToDataDirectory(this.BUNDLE_APPLICATION_FILE, JSON.stringify(data));
            logInfo(`[recordBundleApplication] Recorded bundle application: ${bundleId}`, 0);
        } catch (error) {
            logError(error, '[recordBundleApplication] Failed to record bundle application');
        }
    }

    private async getPendingBundleInfo(): Promise<PendingBundleInfo | null> {
        try {
            // Read plain text JSON file
            const jsonString = await readTextFileFromDataDirectory(this.PENDING_BUNDLE_FILE);
            
            if (!jsonString) {
                logInfo('[getPendingBundleInfo] No pending bundle file found', 2);
                return null;
            }
            
            // Parse the JSON
            const data = JSON.parse(jsonString);
            logInfo(`[getPendingBundleInfo] Read data: ${JSON.stringify(data)}`, 1);
            
            // Validate data is an object
            if (!data || typeof data !== 'object') {
                logInfo('[getPendingBundleInfo] Data is not a valid object', 1);
                return null;
            }
            
            // Validate required fields exist
            const pendingInfo = data as PendingBundleInfo;
            if (!pendingInfo.bundleId || !pendingInfo.version) {
                logInfo(`[getPendingBundleInfo] Missing required fields - bundleId: ${pendingInfo.bundleId}, version: ${pendingInfo.version}`, 1);
                return null;
            }
            
            return pendingInfo;
        } catch (error: any) {
            logError(error, '[getPendingBundleInfo] Failed to read or parse pending bundle info');
            return null;
        }
    }

    private async setPendingBundleInfo(bundleId: string, version: string): Promise<void> {
        const info: PendingBundleInfo = {
            bundleId,
            version,
            downloadedAt: Date.now()
        };
        const jsonString = JSON.stringify(info);
        logInfo(`[setPendingBundleInfo] Writing pending bundle info: ${jsonString}`, 0);
        
        await writeFileToDataDirectory(this.PENDING_BUNDLE_FILE, jsonString);
        logInfo(`[setPendingBundleInfo] Marked bundle ${bundleId} as pending`, 0);
        
        // Verify the write was successful by reading it back
        try {
            const verification = await readTextFileFromDataDirectory(this.PENDING_BUNDLE_FILE);
            if (verification === jsonString) {
                logInfo(`[setPendingBundleInfo] Verified pending bundle file was written correctly`, 1);
            } else {
                logInfo(`[setPendingBundleInfo] WARNING: Verification mismatch! Written: ${jsonString}, Read: ${verification}`, 0);
            }
        } catch (verifyError) {
            logInfo(`[setPendingBundleInfo] WARNING: Could not verify pending bundle file`, 0);
        }
    }

    private async clearPendingBundleInfo(): Promise<void> {
        try {
            const { Filesystem, Directory } = await import('@capacitor/filesystem');
            await Filesystem.deleteFile({
                path: this.PENDING_BUNDLE_FILE,
                directory: Directory.Data
            });
            logInfo('[clearPendingBundleInfo] Cleared pending bundle flag', 1);
        } catch (error) {
            // File doesn't exist, that's fine
        }
    }

    public async shouldSkipUpdateCheck(): Promise<boolean> {
        // Note: The main skip flag check is now done in main.ts BEFORE this method is called
        // This is a secondary check for any remaining flags or cooldown period
        try {
            // Check if a bundle was applied recently (cooldown period)
            const recentlyApplied = await this.wasBundleAppliedRecently();
            if (recentlyApplied) {
                logInfo('[shouldSkipUpdateCheck] Bundle was applied recently - cooldown active', 0);
                return true;
            }
            
            // Double-check for skip flag (should already be cleared by main.ts, but just in case)
            const flagContent = await readTextFileFromDataDirectory('skipUpdateCheck.flag');
            if (flagContent && flagContent.length > 0) {
                logInfo(`[shouldSkipUpdateCheck] Found residual skip flag: ${flagContent}`, 0);
                
                // Clear it
                try {
                    const { Filesystem, Directory } = await import('@capacitor/filesystem');
                    await Filesystem.deleteFile({
                        path: 'skipUpdateCheck.flag',
                        directory: Directory.Data
                    });
                } catch (e) { /* ignore */ }
                
                return true;
            }
            
            return false;
        } catch (error) {
            logInfo('[shouldSkipUpdateCheck] Error (probably no flags exist)', 2);
            return false;
        }
    }

    private async showUpdateAvailableToast(): Promise<void> {
        if (!shouldShowRestartToast()) {
            logInfo('[showUpdateAvailableToast] Skipping (restart toast already shown this session, e.g. by migration)', 1);
            return;
        }
        try {
            const { toastController } = await import('@ionic/vue');
            const messages = store.getters.getLocaleMessages();
            
            // Use language key if available, otherwise use fallback message
            let toastMessage: string;
            if (messages?.application?.bundleUpdateAvailable) {
                toastMessage = messages.application.bundleUpdateAvailable;
            } else {
                logInfo('[showUpdateAvailableToast] Language key "bundleUpdateAvailable" not found, using fallback message', 0);
                toastMessage = 'Update downloaded. Restart the app to install.';
            }
            
            const toast = await toastController.create({
                message: toastMessage,
                duration: 3000,
                position: 'middle',
                cssClass: 'bundle-update-toast'
            });
            
            await toast.present();
            markRestartToastShown();
            logInfo('[showUpdateAvailableToast] Showed bundle update available toast', 1);
        } catch (error: any) {
            logError(error, '[showUpdateAvailableToast] Failed to show toast');
        }
    }

    public async applyPendingBundleIfExists(): Promise<boolean> {
        logInfo('[Loading] 16% - apply-pending-start', 0);
        
        // Session guard - only allow one bundle application per session
        if (LiveUpdateService.bundleApplicationAttempted) {
            logInfo('[Loading] 16% - already-attempted', 0);
            return false;
        }
        
        // Check if a bundle was applied recently (cooldown period)
        const recentlyApplied = await this.wasBundleAppliedRecently();
        if (recentlyApplied) {
            logInfo('[Loading] 16% - cooldown-active', 0);
            await this.clearPendingBundleInfo();
            return false;
        }
        
        const pendingInfo = await this.getPendingBundleInfo();
        if (!pendingInfo) {
            logInfo('[Loading] 16% - no-pending', 0);
            return false;
        }
        
        logInfo(`[Loading] 16% - found-pending-${pendingInfo.bundleId.substring(0, 8)}`, 0);
        
        // Check if we're already running this bundle version
        const currentBundleId = await this.getCurrentBundleId();
        logInfo(`[Loading] 17% - current-${currentBundleId?.substring(0, 8) || 'none'}`, 0);
        
        if (currentBundleId === pendingInfo.bundleId) {
            logInfo('[Loading] 17% - already-running', 0);
            await this.clearPendingBundleInfo();
            return false;
        }
        
        // Verify the bundle actually exists locally
        const bundleStatus = await LiveUpdate.getBundles();
        const existingBundles = bundleStatus.bundleIds || [];
        
        if (!existingBundles.includes(pendingInfo.bundleId)) {
            logInfo('[Loading] 17% - bundle-not-local', 0);
            await this.clearPendingBundleInfo();
            return false;
        }
        
        // Mark that we're attempting to apply a bundle this session
        LiveUpdateService.bundleApplicationAttempted = true;
        
        logInfo(`[Loading] 18% - applying-${pendingInfo.bundleId.substring(0, 8)}`, 0);
        store.dispatch("setLoadingValue", { value: 0.18, slug: "applying-pending" });
        
        // Reset lastVisitedRoute to /fields
        try {
            const preferences = store.getters.getPreferences();
            preferences.lastVisitedRoute = "/fields";
            await store.dispatch("setPreferences", preferences);
            logInfo('[applyPendingBundleIfExists] Reset lastVisitedRoute to /fields', 1);
        } catch (error: any) {
            logError(error, '[applyPendingBundleIfExists] Failed to reset preferences');
        }
        
        // Clean up old bundles
        await this.cleanupOldBundles(pendingInfo.bundleId);
        
        // Apply the bundle
        await LiveUpdate.setNextBundle({ bundleId: pendingInfo.bundleId });
        store.dispatch('setBundleVersion', pendingInfo.version);
        
        // Clear the pending flag FIRST
        await this.clearPendingBundleInfo();
        logInfo('[Loading] 19% - pending-cleared', 0);
        
        // Record that we just applied a bundle (cooldown mechanism)
        await this.recordBundleApplication(pendingInfo.bundleId);
        logInfo('[Loading] 19% - cooldown-recorded', 0);
        
        // Set flag to skip update check after reload
        // Format: "bundleId|timestamp" - timestamp is used as a circuit breaker
        const timestamp = Date.now();
        const skipFlagContent = `${pendingInfo.bundleId}|${timestamp}`;
        
        // Use direct Filesystem API for maximum reliability
        try {
            const { Filesystem, Directory } = await import('@capacitor/filesystem');
            await Filesystem.writeFile({
                path: 'skipUpdateCheck.flag',
                directory: Directory.Data,
                data: skipFlagContent,
                encoding: 'utf8' as any
            });
            logInfo('[Loading] 19% - skip-flag-set', 0);
            
            // Verify the flag was written
            const verifyResult = await Filesystem.readFile({
                path: 'skipUpdateCheck.flag',
                directory: Directory.Data,
                encoding: 'utf8' as any
            });
            if (verifyResult.data === skipFlagContent) {
                logInfo('[Loading] 19% - skip-flag-verified', 0);
            } else {
                logInfo('[Loading] 19% - SKIP-FLAG-MISMATCH', 0);
            }
        } catch (flagError) {
            logInfo('[Loading] 19% - SKIP-FLAG-ERROR', 0);
            logError(flagError, '[applyPendingBundleIfExists] Failed to write skip flag');
        }
        
        // Wait for filesystem operations to fully complete
        logInfo('[Loading] 19% - waiting-3s', 0);
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        logInfo('[Loading] 20% - RELOADING', 0);
        store.dispatch("setLoadingValue", { value: 0.2, slug: "RELOADING" });
        await LiveUpdate.reload();
        
        return true; // App will reload, code after this won't execute
    }

    private async getManifest(): Promise<Manifest | null> {
        try {
            if (!store.getters.allowsInternetAccess()) {
                logInfo('[getManifest] Skipping S3 (no network access)', 1);
                return null;
            }
            logInfo("[getManifest] Retrieving bundle manifest from S3" , 2);
            
            // ALWAYS download from S3 - this file is tiny (500 bytes) and must be fresh to check for bundle updates
            const response = await getFromS3(store.state.variant, MANIFEST_FILE);
            
            if (response.data && Object.keys(response.data).length > 0) {
                const manifest = response.data as Manifest;
                logInfo("[getManifest] Successfully downloaded bundleManifest.json from S3", 2);
                const nextContentVersion = manifest.contentVersion?.toString?.() ?? '';
                if (nextContentVersion) {
                    const previousContentVersion = store.state.contentVersion ?? '';
                    if (previousContentVersion && previousContentVersion !== nextContentVersion) {
                        logInfo(`[getManifest] contentVersion changed (${previousContentVersion} -> ${nextContentVersion}), reloading journeyset metadata`, 0);
                        await reloadManifestMetaData();
                    }
                    if (previousContentVersion !== nextContentVersion) {
                        store.dispatch("setContentVersion", nextContentVersion);
                    }
                }
                return manifest;
            }
            
            logInfo("[getManifest] No manifest data received from S3", 1);
            return null;
        } catch (error) {
            console.error('Failed to fetch bundle manifest file', error);
            logError(error,'Failed to fetch bundle manifest file');
            return null;
        }
    }

    private async cleanupOldBundles(currentBundleId: string) {
        try {
            logInfo(`[cleanupOldBundles] Cleaning up old bundles, keeping ${currentBundleId}`, 2);
            
            // Get list of all bundle IDs
            const bundleStatus = await LiveUpdate.getBundles();
            const allBundleIds = bundleStatus.bundleIds || [];
            
            // Delete all bundles except the current one and the one we're about to download
            for (const bundleId of allBundleIds) {
                if (bundleId !== currentBundleId && bundleId !== store.state.bundleVersion) {
                    try {
                        await LiveUpdate.deleteBundle({ bundleId: bundleId });
                        logInfo(`[cleanupOldBundles] Deleted old bundle ${bundleId}`, 2);
                    } catch (deleteError) {
                        logError(deleteError, `Failed to delete old bundle ${bundleId}`);
                    }
                }
            }
            
            logInfo(`[cleanupOldBundles] Bundle cleanup completed`, 2);
        } catch (error) {
            logError(error, 'Failed to cleanup old bundles');
            // Don't throw - cleanup failure shouldn't prevent updates
        }
    }

    private async proceedWithUpdate(options?: { backgroundOnly?: boolean }): Promise<boolean> {
        const backgroundOnly = options?.backgroundOnly === true;
        if (store.getters.isSecure() && !store.getters.allowsInternetAccess()) {
            logInfo('[proceedWithUpdate] Skipping (secure app, no network access)', 0);
            return false;
        }
        // Guard against multiple simultaneous calls
        if (LiveUpdateService.updateCheckInProgress) {
            logInfo('[proceedWithUpdate] Update check already in progress, skipping', 0);
            return false;
        }
        if (LiveUpdateService.updateCheckCompleted) {
            logInfo('[proceedWithUpdate] Update check already completed this session, skipping', 0);
            return false;
        }
        
        LiveUpdateService.updateCheckInProgress = true;
        logInfo(`[proceedWithUpdate] Starting update check (guard set, backgroundOnly=${backgroundOnly})`, 0);
        
        logInfo('[Loading] 20% - bundle-update-check', 0);
        store.dispatch("setLoadingValue", { value: 0.2, slug: "bundle-update-check" });

        const manifest = await this.getManifest();
        if (!manifest) {
            LiveUpdateService.updateCheckInProgress = false;
            LiveUpdateService.updateCheckCompleted = true;
            logInfo('[proceedWithUpdate] No manifest, marking complete', 0);
            return false;
        }

        let currentVersion = await this.getCurrentBundleId();
        if (!currentVersion) {
            currentVersion = await this.getPackagedBundleVersion();
            logInfo(`[proceedWithUpdate] No current bundle version found, using packaged bundle version: ${currentVersion}`,0);
        }else{
            logInfo(`[proceedWithUpdate] Current bundle version found: ${currentVersion}`,0);
        }

        if (currentVersion === null || currentVersion === '') {
            logInfo("Unknown current bundle version, skipping update", 1);
            LiveUpdateService.updateCheckInProgress = false;
            LiveUpdateService.updateCheckCompleted = true;
            return false;
        }

        //console.log(`Current bundle version: ${currentVersion}`);
        logInfo(`[proceedWithUpdate] Current bundle version: ${currentVersion}`,0);
        //console.log(`Current bundle version: ${currentVersion}`);
        logInfo(`[proceedWithUpdate] Manifest bundle version: ${manifest.version}`,0);
        
        // Additional check: if versions are exactly equal, definitely skip
        if (currentVersion === manifest.version) {
            logInfo(`[proceedWithUpdate] Current version EQUALS manifest version (${currentVersion}), no update needed`, 0);
            LiveUpdateService.updateCheckInProgress = false;
            LiveUpdateService.updateCheckCompleted = true;
            return false;
        }
        
        if (compareVersions(manifest.version, currentVersion) > 0) {
            const bundleId = manifest.version;
            const bundleName = `bundle_${manifest.version}.zip`;
            const bundleUrl = await getBundleDownloadUrl(bundleName);
            const bundleSigUrl = 'updates';

            // Download new app icon to Directory.Data (platform-specific S3 path); fire-and-forget
            this.downloadAndSaveAppIcon().catch(e => logError(e, '[proceedWithUpdate] App icon download failed'));

            try {
                // Check if bundle already exists locally (from previous background download)
                logInfo(`[proceedWithUpdate] Checking if bundle ${bundleId} already exists locally`, 0);
                const bundleStatus = await LiveUpdate.getBundles();
                const existingBundles = bundleStatus.bundleIds || [];
                const bundleAlreadyExists = existingBundles.includes(bundleId);
                
                if (bundleAlreadyExists) {
                    // Check if this bundle was already applied using native API (not Vuex which resets on reload)
                    const actualCurrentBundle = await this.getCurrentBundleId();
                    logInfo(`[proceedWithUpdate] Bundle exists locally. Current native bundle: ${actualCurrentBundle}, Target bundle: ${bundleId}`, 0);
                    
                    if (actualCurrentBundle === bundleId) {
                        logInfo(`[proceedWithUpdate] Bundle ${bundleId} already applied (matches native getCurrentBundleId), skipping`, 0);
                        LiveUpdateService.updateCheckInProgress = false;
                        LiveUpdateService.updateCheckCompleted = true;
                        return false;
                    }
                    
                    logInfo(`[proceedWithUpdate] Bundle ${bundleId} already downloaded locally, marking as pending`, 0);
                    logInfo('[Loading] 90% - bundle-already-downloaded-marking-pending', 0);
                    store.dispatch("setLoadingValue", { value: 0.9, slug: "bundle-already-downloaded-marking-pending" });
                    
                    // Mark bundle as pending for next app launch
                    await this.setPendingBundleInfo(bundleId, manifest.version);
                    
                    // Show toast to inform user (delay slightly to ensure Ionic is ready)
                    setTimeout(() => this.showUpdateAvailableToast(), 1000);
                    
                    logInfo(`[proceedWithUpdate] Bundle marked as pending, will be applied on next app launch`, 0);
                    LiveUpdateService.updateCheckInProgress = false;
                    LiveUpdateService.updateCheckCompleted = true;
                    return false; // No immediate update, continue to app
                } else {
                    // Bundle doesn't exist, need to download it
                    // Non-secure launch (backgroundOnly): always download in background and show toast when done; don't block
                    if (backgroundOnly) {
                        logInfo(`[proceedWithUpdate] Non-secure launch: downloading bundle ${bundleId} in background`, 0);
                        logInfo('[Loading] 100% - bundle-download-backgrounded', 0);
                        store.dispatch("setLoadingValue", { value: 1, slug: "bundle-download-backgrounded" });
                        this.downloadBundleInBackground(bundleUrl, bundleId, bundleSigUrl, manifest.version).catch(error => {
                            logError(error, '[proceedWithUpdate] Background bundle download failed');
                        });
                        LiveUpdateService.updateCheckInProgress = false;
                        LiveUpdateService.updateCheckCompleted = true;
                        return false;
                    }
                    // Secure "Check for Updates" or legacy path: check network speed, then foreground or background
                    logInfo(`[proceedWithUpdate] Bundle ${bundleId} not found locally, checking network speed first`, 0);
                    const shouldAttemptDownload = await networkSpeedService.shouldAttemptDownload('bundle');
                    
                    if (!shouldAttemptDownload) {
                        // Network too slow - background everything (cleanup, signature, download)
                        logInfo(`[proceedWithUpdate] Network too slow for bundle download (8MB), starting background download`, 0);
                        logInfo('[Loading] 100% - bundle-download-backgrounded', 0);
                        store.dispatch("setLoadingValue", { value: 1, slug: "bundle-download-backgrounded" });
                        this.downloadBundleInBackground(bundleUrl, bundleId, bundleSigUrl, manifest.version).catch(error => {
                            logError(error, '[proceedWithUpdate] Background bundle download failed');
                        });
                        LiveUpdateService.updateCheckInProgress = false;
                        LiveUpdateService.updateCheckCompleted = true;
                        return false;
                    }
                    
                    // Network is fast enough - proceed with foreground download
                    logInfo(`[proceedWithUpdate] Network fast enough, downloading bundle from ${bundleUrl}`, 0);
                    logInfo('[Loading] 50% - bundle-download-start', 0);
                    store.dispatch("setLoadingValue", { value: 0.5, slug: "bundle-download-start" });

                    // Clean up old bundles to prevent storage bloat
                    await this.cleanupOldBundles(bundleId);
                    
                    // Delete existing bundle if it exists to avoid "bundle already exists" error
                    try {
                        await LiveUpdate.deleteBundle({ bundleId: bundleId });
                        logInfo(`[proceedWithUpdate] Deleted existing bundle ${bundleId}`, 0);
                    } catch (deleteError) {
                        // Bundle might not exist, which is fine
                        logInfo(`[proceedWithUpdate] No existing bundle to delete for ${bundleId}`, 0);
                    }
                    
                    // Download signature (now only happens after speed check passed)
                    // Use 30 second timeout for foreground download
                    logInfo(`[proceedWithUpdate] Downloading signature from ${bundleSigUrl}`, 0);
                    const signature = await downloadSignatureAsBase64(bundleSigUrl, `bundle_${manifest.version}.sig`, false, 30000);
                    logInfo(`[proceedWithUpdate] Downloaded signature successfully`, 0);
                    
                    if(!signature || signature.length === 0){
                        logInfo(`[proceedWithUpdate] Signature is empty, aborting update`, 0);
                        LiveUpdateService.updateCheckInProgress = false;
                        LiveUpdateService.updateCheckCompleted = true;
                        return false;
                    }
                    
                    // Download the bundle
                    await LiveUpdate.downloadBundle({
                        url: bundleUrl,
                        bundleId: bundleId,
                        signature: signature
                    });
                    logInfo('[Loading] 90% - bundle-downloaded-marking-pending', 0);
                    store.dispatch("setLoadingValue", { value: 0.9, slug: "bundle-downloaded-marking-pending" });
                    
                    logInfo(`[proceedWithUpdate] Bundle ${bundleName} downloaded successfully`, 1);
                    
                    // Mark bundle as pending for next app launch
                    await this.setPendingBundleInfo(bundleId, manifest.version);
                    
                    // Show toast to inform user (delay slightly to ensure Ionic is ready)
                    setTimeout(() => this.showUpdateAvailableToast(), 1000);
                    
                    logInfo(`[proceedWithUpdate] Bundle marked as pending, will be applied on next app launch`, 0);
                    LiveUpdateService.updateCheckInProgress = false;
                    LiveUpdateService.updateCheckCompleted = true;
                    return false; // No immediate update, continue to app
                }
            } catch (error) {
                logError(error,'[proceedWithUpdate] Failed to download or set bundle');
                console.error('[proceedWithUpdate] Failed to download or set bundle ${error.message}');
                logInfo('[Loading] 100% - bundle-update-failed', 0);
                store.dispatch("setLoadingValue", { value: 1, slug: "bundle-update-failed" });
                LiveUpdateService.updateCheckInProgress = false;
                LiveUpdateService.updateCheckCompleted = true;
                return false;
            }
        }else{
            logInfo('[proceedWithUpdate] No updates available. Current version is up to date.',0);
            LiveUpdateService.updateCheckInProgress = false;
            LiveUpdateService.updateCheckCompleted = true;
            return false; // No bundle update needed
        }
    }

    public async getPackagedBundleVersion(): Promise<string> {
        try {
            const res = await fetch('assets/appBundle.json', { cache: 'no-store' });
            if (!res.ok) return '';
            const data = await res.json();
            return (data?.bundleVersion ?? '').toString();
        } catch (e: any) {
            logInfo(`[main] Error reading packaged bundleVersion: ${e?.message || e}`, 1);
            return '';
        }
    }

    /** Secure Android offline-only SKU: true only when assets/offlineSecure.json has offlineOnly: true */
    public async getPackagedOfflineOnlySecure(): Promise<boolean> {
        try {
            const res = await fetch('assets/offlineSecure.json', { cache: 'no-store' });
            if (!res.ok) {
                return false;
            }
            const data = await res.json();
            return data?.offlineOnly === true;
        } catch (e: any) {
            logInfo(`[LiveUpdateService] Error reading offlineSecure.json: ${e?.message || e}`, 1);
            return false;
        }
    }

    public async getCurrentBundleId(): Promise<string | null> {
        try {
            const result = await LiveUpdate.getCurrentBundle();
            return result.bundleId;
        } catch (error) {
            logError(error, 'Failed to get current bundle ID');
            return null;
        }
    }

    /**
     * Returns the S3 key (under variant) for the app icon for the current platform.
     * iOS: icons/ios/AppIcon.appiconset/Icon-App-60x60@3x.png
     * Android secure: icons/android/secure/playstore-icon.png
     * Android non-secure / PWA: icons/android/public/playstore-icon.png (PWA always uses public).
     */
    private async getAppIconS3Key(): Promise<string> {
        const platform = store.getters.platform();
        if (platform === 'web') {
            return APP_ICON_S3_KEYS.androidPublic;
        }
        if (platform === 'ios') {
            return APP_ICON_S3_KEYS.ios;
        }
        if (platform === 'android') {
            return store.getters.isSecure() ? APP_ICON_S3_KEYS.androidSecure : APP_ICON_S3_KEYS.androidPublic;
        }
        return APP_ICON_S3_KEYS.androidPublic;
    }

    /**
     * Native only. Checks whether appIcon.png exists in Directory.Data and downloads it from S3 if absent.
     * Called on every native launch after language is loaded (variant is set). The stat() is cheap so
     * this is effectively a no-op when the file is already present.
     */
    public async restoreAppIconIfMissing(): Promise<void> {
        try {
            const { Filesystem, Directory } = await import('@capacitor/filesystem');
            try {
                await Filesystem.stat({ path: APP_ICON_DATA_FILE, directory: Directory.Data });
                logInfo('[restoreAppIconIfMissing] appIcon.png present, no restore needed', 1);
                return;
            } catch {
                logInfo('[restoreAppIconIfMissing] appIcon.png missing, downloading from S3', 1);
            }
            await this.downloadAndSaveAppIcon();
        } catch (error) {
            logError(error, '[restoreAppIconIfMissing] Unexpected error');
        }
    }

    /**
     * Download the app icon from S3 (variant-specific path by platform) and save to Directory.Data as appIcon.png.
     * For PWA when store.variant is not set yet, uses variant from hostname subdomain so the loading screen can show the icon early.
     */
    public async downloadAndSaveAppIcon(): Promise<boolean> {
        try {
            if (!store.getters.allowsInternetAccess()) {
                logInfo('[downloadAndSaveAppIcon] Skipping (no network access)', 1);
                return false;
            }
            // PWA: use hostname-derived variant when store not yet set (early load before language); native: always use store
            const variant = store.state.variant || (store.getters.isPwa() ? getPwaVariantFromHostname() : '');
            if (!variant) {
                logInfo('[downloadAndSaveAppIcon] No variant, skipping', 1);
                return false;
            }
            const key = await this.getAppIconS3Key();
            logInfo(`[downloadAndSaveAppIcon] Fetching icon: ${variant}/${key}`, 1);
            const response = await getFromS3(variant, key, 'blob', false, 15000);
            const blob = response?.data;
            if (!blob || !(blob instanceof Blob)) {
                logInfo('[downloadAndSaveAppIcon] No blob data received', 1);
                return false;
            }
            const ok = await writeImageBlobToDataDirectory(APP_ICON_DATA_FILE, blob);
            if (ok) {
                logInfo('[downloadAndSaveAppIcon] Saved appIcon.png to Directory.Data', 1);
            }
            return ok;
        } catch (error) {
            logError(error, '[downloadAndSaveAppIcon] Failed to download or save app icon');
            return false;
        }
    }

    /**
     * Check for language file update and optionally download it.
     * @param options.forceDownload - When true (secure app "Check for Updates"), download and save language file
     *        before manifest/bundle update so the new appVersion is in place. When false, only compare versions
     *        (non-secure path may use background download elsewhere).
     */
    public async checkForLanguageUpdate(options?: { forceDownload?: boolean }) {
        const forceDownload = options?.forceDownload === true;
        if (!store.getters.allowsInternetAccess()) {
            logInfo('[checkForLanguageUpdate] Skipping (no network access)', 0);
            return;
        }
        try {
            const result = await getFromS3(store.state.variant, `appVersion.txt?timestamp=${new Date().getTime()}`, 'text');
            const s3LanguageVersion = parseInt(result.data, 10);
            if (!Number.isFinite(s3LanguageVersion)) {
                logInfo(`[checkForLanguageUpdate] Invalid S3 appVersion, skipping language update`, 0);
                return;
            }
            if (s3LanguageVersion > store.state.appVersion) {
                logInfo(`[checkForLanguageUpdate] Language update available. Current: ${store.state.appVersion}, S3: ${s3LanguageVersion}`, 0);
                if (forceDownload) {
                    logInfo(`[checkForLanguageUpdate] Downloading and saving language file (secure update flow)`, 0);
                    await reloadLanguage();
                    logInfo(`[checkForLanguageUpdate] Language file updated`, 0);
                } else {
                    logInfo(`[checkForLanguageUpdate] Starting background download of language file (non-secure path)`, 0);
                    scheduleLanguageDownloadInBackground(); // fire-and-forget: download, save to cache, apply on next restart
                }
            } else {
                logInfo(`[checkForLanguageUpdate] Language file up to date (${store.state.appVersion})`, 0);
            }
        } catch (error) {
            logError(error, '[checkForLanguageUpdate] Failed to check or download language update');
        }
    }

    public async  fetchSignatureAsBase64(sigUrl: string): Promise<string> {
        //console.log(`[fetchSignatureAsBase64] Fetching signature from ${sigUrl}`);
        try{ 
            const response = await fetch(sigUrl);
            if (!response.ok) {
                logInfo(`[fetchSignatureAsBase64] Failed to download signature from ${sigUrl}`,0);
                logInfo(`[fetchSignatureAsBase64] Status: ${response.status} ${response.statusText}`,0);
                //throw new Error(`Failed to download signature: ${response.statusText}`);
            }
            const arrayBuffer = await response.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);

            // Convert to base64
            let binary = "";
            for (let i = 0; i < bytes.length; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            const signature = btoa(binary);
            //console.log(`[fetchSignatureAsBase64] Signature: ${signature ?? 'empty signature'}`);
            return signature;
        }catch(error: any){
            logError(error,`[fetchSignatureAsBase64] Exception downloading signature from ${sigUrl}`);
            console.error(`[fetchSignatureAsBase64] Exception downloading signature ${error.message}`);
            return '';
        }
        
        
        //  return btoa(binary);
    }

    /**
     * Downloads a bundle in the background without blocking the UI
     * Used when the network is too slow to download during app startup
     */
    private async downloadBundleInBackground(bundleUrl: string, bundleId: string, bundleSigUrl: string, version: string): Promise<void> {
        if (!store.getters.allowsInternetAccess()) {
            logInfo('[downloadBundleInBackground] Skipping (no network access)', 0);
            return;
        }
        logInfo('[downloadBundleInBackground] Starting background download of bundle', 0);
        
        try {
            // Clean up old bundles first
            await this.cleanupOldBundles(bundleId);
            
            // Delete existing bundle if it exists to avoid "bundle already exists" error
            try {
                await LiveUpdate.deleteBundle({ bundleId: bundleId });
                logInfo(`[downloadBundleInBackground] Deleted existing bundle ${bundleId}`, 1);
            } catch (deleteError) {
                // Bundle might not exist, which is fine
                logInfo(`[downloadBundleInBackground] No existing bundle to delete for ${bundleId}`, 1);
            }
            
            // Download signature with extended timeout for background download
            // Use 2 minute timeout since other large downloads may be in progress on slow connections
            logInfo(`[downloadBundleInBackground] Downloading signature from ${bundleSigUrl}`, 1);
            const signature = await downloadSignatureAsBase64(bundleSigUrl, `bundle_${version}.sig`, false, 120000);
            
            if (!signature || signature.length === 0) {
                logError(new Error('Empty signature'), '[downloadBundleInBackground] Signature is empty, aborting background download');
                return;
            }
            
            logInfo(`[downloadBundleInBackground] Signature downloaded, starting bundle download from ${bundleUrl}`, 1);
            
            // Download the bundle with 30 minute timeout
            await LiveUpdate.downloadBundle({
                url: bundleUrl,
                bundleId: bundleId,
                signature: signature
            });
            
            logInfo('[downloadBundleInBackground] Successfully downloaded and cached bundle', 0);
            
            // Mark bundle as pending for next app launch
            await this.setPendingBundleInfo(bundleId, version);
            
            // Show toast to inform user (delay to ensure Ionic is fully ready)
            // Background downloads complete while app is running, so Ionic should be ready
            setTimeout(() => this.showUpdateAvailableToast(), 500);
            
            logInfo('[downloadBundleInBackground] Bundle marked as pending, will be applied on next app launch', 0);
            
        } catch (error: any) {
            logError(error, '[downloadBundleInBackground] Failed to download bundle in background');
            // Don't throw - this is a background operation, we don't want to crash the app
        }
    }

    public async checkForUpdates(): Promise<boolean> {
        try {
                logInfo('[checkForUpdates] Checking for updates',0);
                if (store.getters.isSecure() && !store.getters.allowsInternetAccess()) {
                    logInfo('[checkForUpdates] Skipping (secure app, no network access)', 0);
                    return false;
                }
                if (store.getters.isSecure()) {
                    const SECURE_UPDATE_TIMEOUT_MS = 60000;
                    const alert = new AlertBuilder('custom-alert', `<p>Connect to the internet to check for updates?</p>`)
                        .addNoButton()
                        .addYesButton(async () => {
                            // AlertBuilder now closes the alert immediately (void handler), so wait briefly
                            // for the alert dismiss animation to finish before presenting the loading overlay.
                            await new Promise<void>(resolve => setTimeout(resolve, 300));
                            const loading = await createLoadSpinner();
                            try {
                                await loading.present();
                                logInfo('[checkForUpdates] User opted to update app', 0);
                                store.dispatch("setSecureUpdateOverride", true);
                                const runSecureUpdate = async (): Promise<void> => {
                                    // 1. Language: download language_encrypted.json and save as language.json
                                    logInfo('[checkForUpdates] Downloading language file', 0);
                                    await this.checkForLanguageUpdate({ forceDownload: true });
                                    // 2. Metadata: skip speed check so we don't hang on second "Check For Updates"
                                    logInfo('[checkForUpdates] Downloading metadata file', 0);
                                    await checkAndUpdateManifestWithSpeedCheck({ skipSpeedCheck: true });
                                    // 3. App icon: always re-download on a manual secure check
                                    logInfo('[checkForUpdates] Downloading app icon', 0);
                                    await this.downloadAndSaveAppIcon().catch(e =>
                                        logError(e, '[checkForUpdates] App icon download failed')
                                    );
                                    // 4. Bundle: reset guards so manual check always re-fetches bundleManifest
                                    LiveUpdateService.updateCheckCompleted = false;
                                    LiveUpdateService.updateCheckInProgress = false;
                                    logInfo('[checkForUpdates] Checking and downloading bundle', 0);
                                    await this.proceedWithUpdate();
                                    logInfo('[checkForUpdates] Secure app update check complete', 0);
                                };
                                const timeoutPromise = new Promise<never>((_, reject) =>
                                    setTimeout(() => reject(new Error('SECURE_UPDATE_TIMEOUT')), SECURE_UPDATE_TIMEOUT_MS)
                                );
                                await Promise.race([runSecureUpdate(), timeoutPromise]);
                            } catch (error) {
                                const err = error as Error;
                                if (err?.message === 'SECURE_UPDATE_TIMEOUT') {
                                    logInfo('[checkForUpdates] Secure update timed out, dismissing spinner', 0);
                                    LiveUpdateService.updateCheckInProgress = false;
                                    LiveUpdateService.updateCheckCompleted = false;
                                } else {
                                    logError(err, '[checkForUpdates] Secure update failed');
                                }
                            } finally {
                                await loading.dismiss().catch(e =>
                                    logError(e, '[checkForUpdates] Failed to dismiss loading spinner')
                                );
                            }
                        });
                    await alert.present();
                    return false; // Alert shown, no immediate update
                } else {
                    // Non-secure: on launch check S3 bundleManifest vs current bundleId; if update available download in background and show toast to restart
                    await checkAndUpdateManifestWithSpeedCheck();
                    await this.proceedWithUpdate({ backgroundOnly: true });
                    
                    // Check for language updates (bundle updates are now deferred to next launch)
                    await this.checkForLanguageUpdate();
                    
                    return false; // Bundle updates are deferred, no immediate reload
                }
            } catch (error) {
                logError(error,'Failed to check for updates');
                return false;
            }
    }

    /**
     * PWA helper: always fetch bundleManifest.json to detect contentVersion changes.
     * Uses the same getManifest() path so contentVersion handling stays consistent.
     */
    public async checkBundleManifestForContentVersion(): Promise<void> {
        try {
            await this.getManifest();
        } catch (error) {
            logError(error, '[checkBundleManifestForContentVersion] Failed to fetch bundleManifest');
        }
    }
}
